import { useId } from 'react';
import { Alert, AlertTitle, Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ScanRunSummary } from '../../services/testService';

/**
 * REV-1145 (Karl, 2026-10-26): the public page that says what AFixtBot is, who
 * to contact, and how to allow it through a WAF. The crawler's User-Agent
 * carries the same URL (honu-spider app/services/htmlFetching/userAgent.js).
 */
export const BOT_INFO_URL = 'https://afixt.com/bot';

/**
 * REV-1145: the way out of a blocked crawl — the allowlisting guide. An anchor
 * because it navigates; it is the one link in a discovery notice, since the
 * blocked origins stay plain text.
 */
export function DiscoveryGuideLink() {
  const { t } = useTranslation();
  return (
    <Typography variant="body2" sx={{ mt: 1 }}>
      <Link href={BOT_INFO_URL}>{t('discovery.guideLink', 'How to allowlist AFixtBot')}</Link>
    </Typography>
  );
}

interface DiscoveryOutcomeProps {
  discovery: ScanRunSummary['discovery'];
}

/**
 * REV-944: a site-wide crawl that bot protection partly blocked, as the API
 * reported it (revenant-api src/utils/discoveryOutcome.js). The run completed,
 * but some of the pages the crawl found were never scanned, and nothing else
 * on the page would say so.
 *
 * Only `partially_blocked` renders here. A fully blocked run is `failed`, and
 * its error message already names the origins and the remedy, so a second
 * notice would repeat it. `null` means the run had no delegated crawl.
 *
 * A named region with a heading, not MUI's default `role="alert"`: this is
 * present when the page loads, so it is not an interruption to announce, and
 * a heading lets a screen reader user find it. Origins are plain text — they
 * are what the crawl was pointed at, not navigation anyone has validated.
 */
export function DiscoveryNotice({ discovery }: DiscoveryOutcomeProps) {
  const { t } = useTranslation();
  const headingId = useId();
  if (discovery?.outcome !== 'partially_blocked') return null;

  return (
    <Alert
      severity="warning"
      role="region"
      aria-labelledby={headingId}
      component="section"
      sx={{ mt: 2 }}
    >
      <AlertTitle id={headingId} component="h2">
        {t('discovery.partiallyBlocked.heading', 'Some pages were not scanned')}
      </AlertTitle>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {t('discovery.partiallyBlocked.body', {
          defaultValue:
            "Bot protection blocked {{blocked}} of {{discovered}} discovered pages on {{origins}}, so those pages were not scanned. Allowlist the crawler (AFixtBot) in the site's bot protection and run the scan again.",
          blocked: discovery.blockedPages,
          discovered: discovery.discoveredPages,
          origins: discovery.blockedOrigins.join(', '),
        })}
      </Typography>
      <DiscoveryGuideLink />
    </Alert>
  );
}

/**
 * The same, as a short label for the run history table. Text, so the flag
 * does not depend on colour; nothing for a run with no crawl or a clean one.
 */
export function DiscoverySummary({ discovery }: DiscoveryOutcomeProps) {
  const { t } = useTranslation();
  if (discovery?.outcome === 'blocked') {
    return (
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
        {t('discovery.summary.blocked', 'Blocked by bot protection')}
      </Typography>
    );
  }
  if (discovery?.outcome === 'partially_blocked') {
    return (
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
        {t('discovery.summary.partiallyBlocked', 'Partly blocked by bot protection')}
      </Typography>
    );
  }
  return null;
}
